import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BillingHistory,
  BillingState,
  CancelSubscriptionInput,
  ChangePlanInput,
  CheckoutHandoff,
  MeteredResource,
  PlanChangeResult,
  ResourceUsageReport,
  ResourceUsageView,
  StartSubscriptionInput,
  SubscriptionSummary,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { useAuth } from '../auth/auth-context.js';
import { changePlan, getResourceUsage, getSubscription } from './billing-api.js';
import {
  cancelSubscription,
  getBillingHistory,
  getBillingState,
  startCheckout,
} from './payments-api.js';

/**
 * The billing screen's data layer.
 *
 * Nothing here is queueable offline, and that is deliberate: changing what a shop pays is a
 * seated, considered act, and applying one silently twenty minutes later — possibly after the
 * owner changed their mind — is exactly the surprise a bill should never contain.
 *
 * A successful change reloads the SESSION as well as the query. The plan lives on the session
 * snapshot that every entitlement decision in the browser reads, so without that reload the
 * new tier would be on the billing screen and nowhere else — navigation, buttons and prompts
 * would all still be answering from the old plan.
 */

export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => ['billing', 'subscription'] as const,
  usage: () => ['billing', 'usage'] as const,
};

/**
 * The metered allowances, for the in-module affordances.
 *
 * Deliberately stale-tolerant. This figure decorates a screen somebody opened to do something
 * else, so re-fetching it on every window focus would spend a request an owner never asked for
 * — and being one customer out of date costs nothing, because the API is what actually
 * refuses. What must NOT go stale is the reading right after a create, which is why every
 * write that spends an allowance invalidates this key.
 */
export function useResourceUsage(
  options: { enabled?: boolean } = {},
): UseQueryResult<ResourceUsageReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: billingKeys.usage(),
    queryFn: () => getResourceUsage(client),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** One meter out of the report, for a screen that cares about exactly one of them. */
export function useQuota(resource: MeteredResource): ResourceUsageView | undefined {
  const { data } = useResourceUsage();
  return data?.resources.find((row) => row.resource === resource);
}

export function useSubscription(
  options: { enabled?: boolean } = {},
): UseQueryResult<SubscriptionSummary> {
  const client = useApiClient();
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => getSubscription(client),
    enabled: options.enabled ?? true,
  });
}

export function useChangePlan(): UseMutationResult<PlanChangeResult, unknown, ChangePlanInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { reload } = useAuth();

  return useMutation({
    mutationFn: (input: ChangePlanInput) => changePlan(client, input),
    onSuccess: (result) => {
      queryClient.setQueryData(billingKeys.subscription(), result.subscription);
      // The seat allowance on the roster moved with the plan, and so did every module gate.
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      void reload();
    },
  });
}

// --- Paying for it (task 24) -----------------------------------------------------------------

/**
 * The payment half of the billing screen.
 *
 * `useBillingState` is separate from `useSubscription` rather than merged into it because the
 * two answer different questions and one of them is refused to a shop that has no gateway
 * subscription at all. Merging them would mean a shop still on trial could not see its plan
 * table until it had a mandate.
 */
export const paymentKeys = {
  state: () => ['billing', 'state'] as const,
  history: () => ['billing', 'history'] as const,
};

export function useBillingState(options: { enabled?: boolean } = {}): UseQueryResult<BillingState> {
  const client = useApiClient();
  return useQuery({
    queryKey: paymentKeys.state(),
    queryFn: () => getBillingState(client),
    enabled: options.enabled ?? true,
  });
}

export function useBillingHistory(
  options: { enabled?: boolean } = {},
): UseQueryResult<BillingHistory> {
  const client = useApiClient();
  return useQuery({
    queryKey: paymentKeys.history(),
    queryFn: () => getBillingHistory(client),
    enabled: options.enabled ?? true,
  });
}

/**
 * Start paying.
 *
 * The mutation's job ends at the handoff: it returns the gateway's URL and the caller sends the
 * owner there. Nothing is marked paid here, because nothing IS paid until the gateway tells the
 * server so — see `payments-api.ts`.
 */
export function useStartCheckout(): UseMutationResult<
  CheckoutHandoff,
  unknown,
  StartSubscriptionInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartSubscriptionInput) => startCheckout(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useCancelSubscription(): UseMutationResult<
  BillingState,
  unknown,
  CancelSubscriptionInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { reload } = useAuth();

  return useMutation({
    mutationFn: (input: CancelSubscriptionInput) => cancelSubscription(client, input),
    onSuccess: (state) => {
      queryClient.setQueryData(paymentKeys.state(), state);
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      // Access may have changed this instant, and every gate in the browser reads the session.
      void reload();
    },
  });
}
